import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, Icon } from './Icon';
import { useTranslation } from 'react-i18next';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  noPadding?: boolean;
}

export default function Modal({ open, onClose, title, children, size = 'md', noPadding = false }: ModalProps) {
  const { t } = useTranslation();
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!open) return null;

  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative card w-full ${widths[size]} z-10 max-h-[90vh] overflow-y-auto animate-slide-up dark:shadow-2xl ${noPadding ? '' : 'p-6'}`}>
        {noPadding ? (
          <>
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 z-20 btn-icon w-7 h-7 bg-black/30 hover:bg-black/50 text-white border-0"
              aria-label={t('common.close')}
            >
              <Icon as={X} size={16} />
            </button>
            {children}
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-semibold text-base text-slate-900 dark:text-slate-100">{title}</h2>
              <button
                type="button"
                onClick={onClose}
                className="btn-icon w-7 h-7 text-slate-400 hover:text-slate-700"
                aria-label={t('common.close')}
              >
                <Icon as={X} size={16} />
              </button>
            </div>
            {children}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
